'use client'

import { WagmiAdapter } from '@reown/appkit-adapter-wagmi'
import { createSIWEConfig, formatMessage } from '@reown/appkit-siwe'
import { mainnet } from '@reown/appkit/networks'
import { createAppKit } from '@reown/appkit/react'
import { http } from 'viem'

const projectId = process.env.NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID ?? ''
const SIWE_SERVER_URL =
	process.env.NEXT_PUBLIC_SIWE_SERVICE_URL ?? 'https://siwe-auth-production.up.railway.app'

const metadata = {
	name: 'OnAutopilot',
	description: 'Prediction market aggregator',
	url: 'https://app.onautopilot.finance',
	icons: ['https://app.onautopilot.finance/logos/icon_2.png'],
}

// Store for passing data between SIWE callbacks and auth completion
export const siweAuthStore = {
	targetPublicKey: null as string | null,
	jwt: null as string | null,
	onAuthComplete: null as ((jwt: string) => void) | null,
}

const siweConfig = createSIWEConfig({
	getNonce: async () => {
		const response = await fetch(`${SIWE_SERVER_URL}/nonce`)
		if (!response.ok) throw new Error('Failed to fetch nonce')
		const data = await response.json()
		return data.nonce
	},

	getMessageParams: async () => {
		return {
			domain: typeof window !== 'undefined' ? window.location.host : 'app.onautopilot.finance',
			uri:
				typeof window !== 'undefined' ? window.location.origin : 'https://app.onautopilot.finance',
			chains: [1],
			statement: 'Sign in to OnAutopilot',
		}
	},

	createMessage: ({ address, ...args }) => {
		return formatMessage(args, address)
	},

	verifyMessage: async ({ message, signature }) => {
		try {
			const response = await fetch(`${SIWE_SERVER_URL}/auth/siwe`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					message,
					signature,
					targetPublicKey: siweAuthStore.targetPublicKey,
				}),
			})

			if (!response.ok) {
				return false
			}

			const data = await response.json()
			siweAuthStore.jwt = data.jwt
			return true
		} catch (_err) {
			return false
		}
	},

	getSession: async () => {
		// We don't maintain a persistent SIWE session - we just use it for one-time auth
		return null
	},

	signOut: async () => {
		siweAuthStore.jwt = null
		siweAuthStore.targetPublicKey = null
		return true
	},

	onSignIn: () => {
		// Called after verifyMessage succeeds
		if (siweAuthStore.jwt && siweAuthStore.onAuthComplete) {
			siweAuthStore.onAuthComplete(siweAuthStore.jwt)
		}
	},
})

// Create wagmi adapter
const wagmiAdapter = new WagmiAdapter({
	networks: [mainnet],
	projectId,
	transports: {
		[mainnet.id]: http(),
	},
})

// Only initialize on client
let appKit: ReturnType<typeof createAppKit> | null = null

export function getAppKit() {
	if (typeof window === 'undefined') return null

	if (!appKit) {
		appKit = createAppKit({
			adapters: [wagmiAdapter],
			networks: [mainnet],
			projectId,
			metadata,
			siweConfig,
			features: {
				analytics: false,
				email: false,
				socials: false,
			},
		})
	}

	return appKit
}

export { wagmiAdapter }
